import type { FC, ReactNode } from 'react'
import type { Cart, MenuCategory } from '@/types'

import { CalendarDays, Phone, ShoppingBag } from 'lucide-react'
import { OrderForm } from '@/components/landing/order-form'
import { SectionHeading } from '@/components/landing/section-heading'
import { formatTime, getFirstOpeningTime, LAST_PICKUP_BEFORE_CLOSE_MINUTES } from '@/lib/hours'
import { siteConfig } from '@/constants/site'

interface OrderSectionProps {
    categories: Array<MenuCategory>
    cart: Cart
    onQuantityChange: (itemId: string, quantity: number) => void
    onClear: () => void
}

const perks = [
    {
        icon: CalendarDays,
        title: "Pick a time this week",
        text: `Pickups start at ${formatTime(getFirstOpeningTime())} and run until ${LAST_PICKUP_BEFORE_CLOSE_MINUTES} minutes before we close.`
    },
    {
        icon: ShoppingBag,
        title: "Packed to travel",
        text: "Porridge in a sealed bowl, toppings on the side, so nothing goes soggy on the way home."
    },
    {
        icon: Phone,
        title: "Pay when you pick up",
        text: `Card or cash at the counter. Questions about your order? Call us at ${siteConfig.phone}.`
    }
]

export const OrderSection: FC<OrderSectionProps> = ({ categories, cart, onQuantityChange, onClear }): ReactNode => {
    return (
        <section id='order' aria-labelledby='order-title' className='relative isolate scroll-mt-16 overflow-clip bg-broth py-20 text-broth-foreground sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -left-24 top-10 size-[26rem] rounded-full bg-primary/25 blur-3xl' />
                <div className='absolute -bottom-32 right-0 size-[30rem] rounded-full bg-yolk/10 blur-3xl' />
            </div>

            <div className='mx-auto grid max-w-6xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16'>
                <div className='lg:sticky lg:top-28'>
                    <SectionHeading
                        titleId='order-title'
                        tone='dark'
                        eyebrow="Order ahead"
                        title={<>Skip the line, <em>not the ginger.</em></>}
                        description="Choose your bowls, pick a pickup time and we will have everything hot and packed when you walk in."
                    />

                    <ul className='mt-10 space-y-6'>
                        {perks.map(perk => (
                            <li key={perk.title} className='flex gap-4'>
                                <span className='inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-broth-foreground/10 text-yolk'>
                                    <perk.icon className='size-5' aria-hidden='true' />
                                </span>

                                <span>
                                    <span className='block font-semibold'>
                                        {perk.title}
                                    </span>

                                    <span className='mt-1 block text-sm leading-relaxed text-broth-foreground/70'>
                                        {perk.text}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='rounded-[2rem] bg-card p-6 text-card-foreground shadow-2xl shadow-black/30 sm:p-8'>
                    <OrderForm
                        categories={categories}
                        cart={cart}
                        onQuantityChange={onQuantityChange}
                        onClear={onClear}
                    />
                </div>
            </div>
        </section>
    )
}
